import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Breadcrumb, Button, Alert, Loading, Input, Combobox, Checkbox, FileUploadMultiple } from '../../Components';
import { APIS } from '../../Config/apis';
import { FORM_ERRORS } from "../../Config/form_errors";
import { useLocation } from "react-router-dom";
import userAuth from '../../Config/userauth';
import { GLOBAL } from '../../Config/global';
import { DEFAULT_HEADER } from '../../Config/userToken';

export default function Consultas() {
    const [loadingForm, setLoadingForm] = useState(false);
    const [sectores, setSectores] = useState([]);
    const [tiposConsulta, setTiposConsulta] = useState([]);

    const [tipoConsulta, setTipoConsulta] = useState("");
    const [sector, setSector] = useState("");
    const [motivo, setMotivo] = useState("");
    const [comentario, setComentario] = useState("");
    const [showQueryReason, setShowQueryReason] = useState(true);
    const [copy, setCopy] = useState(false);
    const [sectorMessage, setSectorMessage] = useState("");

    const [errorMsg_tipoConsulta, setErrorMsg_tipoConsulta] = useState('');
    const [borderColor_tipoConsulta, setBorderColor_tipoConsulta] = useState('');
    const [errorMsg_sector, setErrorMsg_sector] = useState('');
    const [borderColor_sector, setBorderColor_sector] = useState('');
    const [errorMsg_comentario, setErrorMsg_comentario] = useState('');
    const [errorBorder_comentario, setErrorBorder_comentario] = useState('');

    const [submitResponse, setSubmitResponse] = useState(null);
    const [submitOk, setSubmitOk] = useState(false);
    const [submitInProcess, setSubmitInProcess] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const refFileUploadMultiple = useRef(null);
    const location = useLocation();

    // Utilidad local para GET con/ sin token
    const fetchJson = useCallback(async (url, useToken = true) => {
        const headers = useToken ? DEFAULT_HEADER.get() : { 'Content-Type': 'application/json' };
        const res = await fetch(url, { method: 'GET', headers });
        if (!res.ok) {
            const text = await res.text();
            let msg = 'Ocurrió un error.';
            try {
                const data = JSON.parse(text);
                msg = data?.message || text;
            } catch {
                msg = text || msg;
            }
            throw new Error(msg);
        }
        return res.json();
    }, []);

    // Cargar sectores al montar + revisar ?data=envioFactura
    useEffect(() => {
        let isMounted = true;

        (async () => {
            try {
                const data = await fetchJson(APIS.URLS.SECTOR, true);
                // Si el backend devuelve array plano, lo mapeamos acá si hace falta.
                // Si ya viene como [{id, value, hasSubCategory, message}], no toques.
                const withEmpty = GLOBAL.COMBO_EMPTY_ITEM
                    ? [GLOBAL.COMBO_EMPTY_ITEM, ...(Array.isArray(data) ? data : [])]
                    : (Array.isArray(data) ? data : []);

                if (isMounted) setSectores(withEmpty);
            } catch (e) {
                // si querés, podés mostrar un Alert arriba con este error
                console.error('Error cargando sectores:', e.message);
            }
        })();

        const urlParams = new URLSearchParams(location.search);
        const param1 = urlParams.get('data');
        const isEnvioFactura = param1 === 'envioFactura';
        if (isEnvioFactura) {
            setShowQueryReason(false);
            setSector("4"); // deshabilita el combo visualmente
            setTipoConsulta(""); // por si quedó algo previo
        }

        return () => { isMounted = false; };
    }, [location.search, fetchJson]);

    // Cuando cambia el sector:
    // - seteo el mensaje
    // - muestro/oculto el combo tipoConsulta según hasSubCategory
    // - cargo tiposConsulta si corresponde
    useEffect(() => {
        if (!sector) {
            setSectorMessage('');
            setShowQueryReason(true);
            setTiposConsulta([]);
            setTipoConsulta('');
            return;
        }

        // Buscar el objeto sector seleccionado para obtener message/hasSubCategory
        const selected = sectores.find(s => String(s.id) === String(sector));
        if (selected) {
            setSectorMessage(selected.message ?? '');
            if (selected.hasSubCategory === false) {
                setShowQueryReason(false);
                setTipoConsulta('');
            } else {
                setShowQueryReason(true);
            }
        } else {
            setSectorMessage('');
            setShowQueryReason(true);
        }

        // Cargar tipos de consulta si el sector está seteado
        (async () => {
            if (!sector) return;
            try {
                const data = await fetchJson(`${APIS.URLS.TIPO_CONSULTA}${sector}`, true);
                const withEmpty = GLOBAL.COMBO_EMPTY_ITEM
                    ? [GLOBAL.COMBO_EMPTY_ITEM, ...(Array.isArray(data) ? data : [])]
                    : (Array.isArray(data) ? data : []);
                setTiposConsulta(withEmpty);
            } catch (e) {
                console.error('Error cargando tipos de consulta:', e.message);
                setTiposConsulta(GLOBAL.COMBO_EMPTY_ITEM ? [GLOBAL.COMBO_EMPTY_ITEM] : []);
            }
        })();
    }, [sector, sectores, fetchJson]);

    const handleChange = (event) => {
        const isCheckbox = event.target.type === "checkbox";
        const inputName = event.target.id ? event.target.id : event.target.name;
        const inputValue = isCheckbox ? event.target.checked : event.target.value;

        switch (inputName) {
            case 'copy':
                setCopy(inputValue);
                break;
            case 'motivo':
                setMotivo(inputValue);
                break;
            case 'comentario':
                setComentario(inputValue);
                break;
            default:
                break;
        }
    };

    const handleComboChange = (selectedOption, event) => {
        if (!selectedOption || selectedOption.value === undefined) return;

        const inputName = event?.name ?? event?.target?.name ?? event?.id;
        const inputValue = selectedOption.value;

        if (inputName === 'sector') {
            setSector(inputValue);
            // El efecto de arriba se encarga del resto (mensaje, hasSubCategory, tiposConsulta)
        }

        if (inputName === 'tipoConsulta') {
            setTipoConsulta(inputValue);
        }
    };

    const handleResponse = (response) => {
        setSubmitResponse(response);
        if (response.success) {
            setLoadingForm(false);
            setSubmitOk(true);
            setTimeout(() => {
                window.location.reload(true);
            }, 5000);
        } else {
            const msg = response.businessExceptions?.map(x => x.message).join(". ") || "Ocurrió un error.";
            setLoadingForm(false);
            setSubmitError(msg);
            setSubmitInProcess(false);
        }
    };

    const handleSubmit = async (event) => {
        debugger
        event.preventDefault();
        const { COMMON_ERRORS } = FORM_ERRORS;

        let errors = false;

        let _errorMsg_tipoConsulta = '';
        let _errorMsg_sector = '';
        let _errorMsg_comentario = '';

        if (showQueryReason && !tipoConsulta) {
            _errorMsg_tipoConsulta = `${COMMON_ERRORS.INITIAL_MARKER} ${COMMON_ERRORS.EMPTY_FIELD}`;
            errors = true;
        }

        if (!sector) {
            _errorMsg_sector = `${COMMON_ERRORS.INITIAL_MARKER} ${COMMON_ERRORS.EMPTY_FIELD}`;
            errors = true;
        }

        if (!comentario) {
            _errorMsg_comentario = `${COMMON_ERRORS.INITIAL_MARKER} ${COMMON_ERRORS.EMPTY_FIELD}`;
            errors = true;
        } else if (comentario.length < 25) {
            _errorMsg_comentario = `${COMMON_ERRORS.INITIAL_MARKER} ${COMMON_ERRORS.FIELD_MIN_CHARACTERS_25}`;
            errors = true;
        }

        setErrorMsg_tipoConsulta(_errorMsg_tipoConsulta);
        setErrorMsg_sector(_errorMsg_sector);
        setErrorMsg_comentario(_errorMsg_comentario);

        setBorderColor_tipoConsulta(_errorMsg_tipoConsulta ? 'error' : 'success');
        setBorderColor_sector(_errorMsg_sector ? 'error' : 'success');
        setErrorBorder_comentario(_errorMsg_comentario ? 'error' : 'success');

        if (errors) return;

        setLoadingForm(true);
        setSubmitOk(false);
        setSubmitError('');
        setSubmitInProcess(true);

        const files = refFileUploadMultiple.current?.getFiles?.() ?? [];

        try {
            const response = await postConsultaMultipart(
                APIS.URLS.CONSULTA_SEND,
                {
                    IdSector: sector,
                    // si no se muestra el combo, mandamos vacío
                    IdSectorQueryReason: showQueryReason ? tipoConsulta : '',
                    Motive: motivo,
                    Detail: comentario,
                    SendCopy: copy,
                    User: userAuth.userId()
                },
                files
            );

            setSubmitResponse(response);
            setLoadingForm(false);
            setSubmitOk(true);

            setTimeout(() => {
                window.location.reload(true);
            }, 5000);
        } catch (e) {
            setLoadingForm(false);
            setSubmitError(e.message || 'Ocurrió un error.');
            setSubmitInProcess(false);
        }
    };

    const getAuthHeaders = () => {
        const h = DEFAULT_HEADER.get?.() ?? {};
        const auth = h['Authorization'] || h['authorization'];
        return auth ? { Authorization: auth } : {};
    };

    const postConsultaMultipart = async (url, payload, fileItems = []) => {
        const fd = new FormData();
        Object.entries(payload).forEach(([k, v]) => {
            const value = v === undefined || v === null ? '' : v;
            fd.append(k, value);
        });

        for (const item of fileItems) {
            fd.append('FileName', item.name);
            fd.append('File', item.file, item.name);
        }

        const res = await fetch(url, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: fd
        });

        const text = await res.text();
        let data;
        try {
            data = text ? JSON.parse(text) : {};
        } catch {
            if (!res.ok) {
                throw new Error(text || 'Error en la respuesta del servidor.');
            }
            data = {};
        }

        if (!res.ok) {
            const msg =
                data?.businessExceptions?.map?.(x => x.message).join('. ') ||
                data?.message ||
                text ||
                'Ocurrió un error.';
            throw new Error(msg);
        }

        return data;
    };

    return (
        <section>
            <Loading visible={loadingForm} />
            <section className="form-container master-page">
                <div className="col-12">
                    <div className="row">
                        <div className="col-12">
                            <Breadcrumb currentPage="/consulta" />
                        </div>
                    </div>
                </div>

                <div className="col-12 form-container container containerMarginMin">
                    <div className="row">
                        <div className="col-2 p-5">
                            <div className="lblCobranzas">{sectorMessage}</div>
                        </div>

                        <div className="col-8 padding10">
                            <form id="consulta_form" onSubmit={handleSubmit}>
                                {submitError && (
                                    <div className="row mb-2">
                                        <div className="col-12">
                                            <Alert
                                                icon="times"
                                                title="Error"
                                                message={submitError}
                                                noDetail={true}
                                                level={submitResponse?.level}
                                            />
                                        </div>
                                    </div>
                                )}

                                {submitOk && (
                                    <div className="row mb-2">
                                        <div className="col-12">
                                            <Alert
                                                type="success"
                                                title="Mensaje enviado"
                                                message="Su comentario será recepcionado y respondido a la brevedad. Muchas gracias."
                                                noDetail={true}
                                            />
                                        </div>
                                    </div>
                                )}

                                <div className="row mb-3">
                                    <div className="col-12">
                                        <Checkbox
                                            id="copy"
                                            checked={copy}
                                            fontSize="smaller-text"
                                            text="Recibir copia en mi e-mail"
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-6">
                                        <Combobox
                                            id="sector"
                                            name="sector"
                                            label="Sector"
                                            options={sectores.map(item => ({
                                                value: item.id,
                                                label: item.value,
                                                hasSubCategory: item.hasSubCategory,
                                                message: item.message
                                            }))}
                                            isDisabled={sector === "4"}
                                            defaultValue={sector}
                                            onChange={handleComboChange}
                                            errorMsg={errorMsg_sector}
                                            borderColor={borderColor_sector}
                                        />
                                    </div>

                                    {showQueryReason && (
                                        <div className="col-6">
                                            <Combobox
                                                id="tipoConsulta"
                                                name="tipoConsulta"
                                                label="Tipo de Consulta"
                                                options={tiposConsulta.map(item => ({ value: item.id, label: item.value }))}
                                                defaultValue={tipoConsulta}
                                                onChange={handleComboChange}
                                                errorMsg={errorMsg_tipoConsulta}
                                                borderColor={borderColor_tipoConsulta}
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="row">
                                    <div className="col-12">
                                        <Input
                                            id="motivo"
                                            type="text"
                                            text="Motivo - Asunto"
                                            defaultValue={motivo}
                                            maxLength="200"
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-12">
                                        <div className={errorMsg_comentario.length ? 'input input-span' : 'input'}>
                                            <label htmlFor="comentario">Comentario</label>
                                            <div className={`input-group input-group-${errorBorder_comentario} rounded`}>
                                                <textarea
                                                    className="form-control"
                                                    id="comentario"
                                                    onChange={handleChange}
                                                    maxLength={2000}
                                                    rows={5}
                                                    value={comentario}
                                                />
                                            </div>
                                            {errorMsg_comentario.length ? (
                                                <span className={`input-span-${errorBorder_comentario}`}>{errorMsg_comentario}</span>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>

                                <div className="row mb-3">
                                    <div className="col-12">
                                        <label htmlFor="comentario">Adjuntar archivo</label>
                                        <div>
                                            <FileUploadMultiple ref={refFileUploadMultiple} multiple={true} />
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    {!submitInProcess && (
                                        <div className="col-12">
                                            <Button type="submit" id="btnSubmit">ENVIAR</Button>
                                        </div>
                                    )}
                                </div>
                            </form>
                        </div>

                        <div className="col-2"></div>
                    </div>
                </div>
            </section>
        </section>
    );
}
